export const STUDIO_SCENARIO_PREVIEW_CHANNELS = [
  "internal_preview",
  "demo_driver",
  "sandbox_rehearsal",
  "voice_quality_lab",
] as const;

export type StudioScenarioPreviewChannel =
  (typeof STUDIO_SCENARIO_PREVIEW_CHANNELS)[number];

export type StudioScenarioPresetId =
  | "receptionist_greeting"
  | "scheduling_assist"
  | "human_handoff"
  | "objection_handling"
  | "premium_voice_polish_preview";

export type StudioScenarioPreset = {
  id: StudioScenarioPresetId;
  label: string;
  summary: string;
  operatorGoal: string;
  channel: StudioScenarioPreviewChannel;
  transcriptLines: string[];
};

export type StudioScenarioPayload = {
  scenarioId: StudioScenarioPresetId;
  scenarioLabel: string;
  channel: StudioScenarioPreviewChannel;
  correlationId: string;
  transcriptLines: string[];
  text: string;
};

export const STUDIO_SCENARIO_PRESETS: StudioScenarioPreset[] = [
  {
    id: "receptionist_greeting",
    label: "Receptionist greeting",
    summary:
      "Demonstrate a polished first hello that sets scope, tone, and next-step clarity.",
    operatorGoal:
      "Show a calm, premium welcome without implying live clinic activation.",
    channel: "internal_preview",
    transcriptLines: [
      "Thank you for calling VetCan.",
      "I can help with hours, scheduling guidance, renewal next steps, or a clean staff handoff.",
      "What would you like to handle first?",
    ],
  },
  {
    id: "scheduling_assist",
    label: "Scheduling assist",
    summary:
      "Preview concise front-desk scheduling guidance while keeping edge cases with staff.",
    operatorGoal:
      "Show how the assistant keeps the next step clear without overpromising booking actions.",
    channel: "demo_driver",
    transcriptLines: [
      "I can keep this simple.",
      "The quickest next step is to confirm whether you are looking for a renewal or a follow-up.",
      "If anything is unusual, I can bring in staff right away so the handoff stays clean.",
    ],
  },
  {
    id: "human_handoff",
    label: "Human handoff",
    summary:
      "Demonstrate an immediate, professional staff handoff when the request needs a person.",
    operatorGoal:
      "Rehearse a calm transition to staff without drifting into medical advice or PHI collection.",
    channel: "sandbox_rehearsal",
    transcriptLines: [
      "I want to make sure this gets handled cleanly.",
      "I am going to bring in a staff member now so you have a direct handoff.",
      "Thank you for staying with me while I connect the next step.",
    ],
  },
  {
    id: "objection_handling",
    label: "Objection handling",
    summary:
      "Preview a warm, business-class response to hesitation or skepticism.",
    operatorGoal:
      "Show calm reassurance and a clear next step without hype or pressure.",
    channel: "demo_driver",
    transcriptLines: [
      "That is a fair question.",
      "I can keep this brief, explain the next step clearly, and involve staff immediately if you want a more specific answer.",
      "Happy to help you choose the simplest path forward.",
    ],
  },
  {
    id: "premium_voice_polish_preview",
    label: "Premium voice polish preview",
    summary:
      "Use the voice-quality lab lane to compare premium pacing, warmth, and close quality.",
    operatorGoal:
      "Inspect a polished talk track in the isolated quality lane before any canary discussion.",
    channel: "voice_quality_lab",
    transcriptLines: [
      "Of course, I can help with that.",
      "The next step is straightforward, and I can keep this clear from start to finish.",
      "Thank you for taking a moment with VetCan today.",
    ],
  },
];

export function getStudioScenarioPreset(
  presetId: StudioScenarioPresetId
): StudioScenarioPreset {
  return (
    STUDIO_SCENARIO_PRESETS.find((preset) => preset.id === presetId) ||
    STUDIO_SCENARIO_PRESETS[0]
  );
}

export function isStudioScenarioPresetId(
  value: string | null | undefined
): value is StudioScenarioPresetId {
  return STUDIO_SCENARIO_PRESETS.some((preset) => preset.id === value);
}

export function buildStudioScenarioPayload(
  preset: StudioScenarioPreset
): StudioScenarioPayload {
  return {
    scenarioId: preset.id,
    scenarioLabel: preset.label,
    channel: preset.channel,
    correlationId: `studio_demo_${preset.id}`,
    transcriptLines: [...preset.transcriptLines],
    text: preset.transcriptLines.join(" "),
  };
}
